<template>
	<v-menu
		v-model="menu"
		:close-on-content-click="false"
		:max-width="$vuetify.display.mdAndUp ? width * 0.25 : width"
		:min-width="width < 430 ? width : 430"
		location="bottom left"
		transition="scale-transition"
	>
		<template #activator="{ props: btnProps }">
			<v-btn stacked v-bind="btnProps">
				<v-badge
					:model-value="notificationsCountUnread !== 0"
					:content="notificationsCountUnread"
				>
					<v-icon
						:icon="
							notificationsCountUnread > 0
								? 'fa-regular fa-bell'
								: 'fa-regular fa-bell-slash'
						"
						color="primary"
					></v-icon>
				</v-badge>
			</v-btn>
		</template>

		<BaseCard
			:title="{ key: 'entity.notification.name', params: { count: 2 } }"
			:subtitle="
				`${notifications.length} / ${notificationsCollection ? itemsTotalLength : '-'} ` +
				t('component.notifications.loaded')
			"
			divider="both"
			action-spacer
		>
			<template #append>
				<ButtonIcon
					v-if="notificationsCountUnread > 0"
					:disabled="mode === 1"
					:loading="updating"
					:tooltip="{
						text: 'component.notifications.markAllAsRead',
						left: true,
						// attach: '#notifications-card',
					}"
					color="error"
					icon="fa-regular fa-check-double"
					icon-size="small"
					notext
					@click.stop="markAllAsRead"
				>
				</ButtonIcon>
				<ButtonIcon
					color="info"
					icon="fa-regular fa-times"
					icon-size="small"
					notext
					@click="menu = false"
				></ButtonIcon>
			</template>

			<v-list
				class="overflow-y-auto"
				density="compact"
				lines="three"
				:max-height="height * 0.6"
			>
				<v-list-item v-if="loading">
					<v-progress-linear indeterminate></v-progress-linear>
				</v-list-item>
				<v-list-item v-if="notifications.length === 0 && !loading">
					<div class="justify-center text-caption">
						{{ t('component.notifications.noneFound', { status }) }}
					</div>
				</v-list-item>
				<template v-for="(notify, index) in notifications" :key="notify.id">
					<v-hover v-if="!loading">
						<template #default="{ isHovering, props: hoverProps }">
							<v-list-item v-bind="hoverProps">
								<template #prepend>
									<v-icon size="small" :icon="notify?.icon"></v-icon>
								</template>
								<div>
									<v-list-item-title>{{ notify.title }}</v-list-item-title>
									<v-list-item-subtitle
										v-if="typeof notify.content.link !== 'undefined'"
										class="notifications-card__subtitle"
										@click="notificationLink(notify.id)"
									>
										<span v-if="typeof notify.content.link.to === 'undefined'">
											{{ notify.content.link.name }}
										</span>
										<router-link v-else :to="notify.content.link.to"
											>{{ notify.content.link.name }}
										</router-link>
									</v-list-item-subtitle>
									<v-list-item-subtitle>
										<v-tooltip
											:text="t('component.notifications.sentOn')"
											location="top"
										>
											<template #activator="{ props }">
												<v-chip
													color="primary"
													label
													size="x-small"
													v-bind="props"
													prepend-icon="fa-regular fa-paper-plane"
													:text="
														getDateTime(notify.content.created_at)
															?.dateTimeLocal
													"
												>
												</v-chip>
											</template>
										</v-tooltip>
										<v-tooltip
											v-if="mode !== 0"
											:text="t('common.readOn')"
											location="top"
										>
											<template #activator="{ props }">
												<v-chip
													class="ml-1"
													color="success"
													label
													size="x-small"
													v-bind="props"
													prepend-icon="fa-regular fa-book-reader"
													:text="
														getDateTime(notify.content.read_at)?.dateTimeLocal
													"
												>
												</v-chip>
											</template>
										</v-tooltip>
									</v-list-item-subtitle>
								</div>

								<template #append>
									<v-list-item-action v-show="isHovering">
										<ButtonIcon
											v-if="isAdmin || isTeam"
											:to="{
												name: 'notificationView',
												params: { id: notify.id },
											}"
											class="mt-3"
											color="primary"
											icon="fa-regular fa-link"
											icon-size="small"
											notext
										></ButtonIcon>
										<ButtonIcon
											:disabled="notify.content.read_at !== null"
											class="mt-3"
											color="error"
											icon="fa-regular fa-check"
											icon-size="small"
											notext
											@click.stop="markAsRead(notify.id)"
										>
										</ButtonIcon>
									</v-list-item-action>
								</template>
							</v-list-item>
						</template>
					</v-hover>
					<v-divider
						v-if="index + 1 < notifications.length"
						:key="`divider_${index}`"
					></v-divider>
				</template>
			</v-list>

			<template #actions>
				<v-btn-toggle :disabled="loading" v-model="mode">
					<v-btn size="small" icon="fa-regular fa-bell-on"> </v-btn>
					<v-btn size="small" icon="fa-regular fa-bell-slash"> </v-btn>
					<v-btn size="small" icon="fa-regular fa-bell"> </v-btn>
				</v-btn-toggle>
				<v-spacer></v-spacer>
			</template>
		</BaseCard>
	</v-menu>
</template>

<script setup lang="ts">
import { useTable } from '@/composables/useTable'
import { useDayJs } from '@/composables/dateTime'
import $collections from '@/constants/collections'
import { personBuildFullname } from '@/filters'
import { useNotificationStore } from '@/stores/modules/notification'
import { useUsersStore } from '@/stores/modules/users'
import { useUserStore } from '@/stores/system'
import { useWindowSize } from '@vueuse/core'
import { getNotificationType } from 'helpers/FormatValue'
import { map } from 'lodash'
import { storeToRefs } from 'pinia'
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue'
import BaseCard from '../templates/Card/BaseCard.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const { getDateTime } = useDayJs()

const { height, width } = useWindowSize()
const notificationStore = useNotificationStore()
const usersStore = useUsersStore()
const { whoami, isAdmin, isTeam } = storeToRefs(useUserStore())

const mode = ref(0)
const menu = ref(false)

const personLinkDefault = {
	link: {
		name: t('component.notifications.noInformationAvailable'),
	},
}
const polling = ref(undefined)
const updating = ref(false)

const {
	itemsTotal: readItems,
	isLoading: readLoading,
	itemsTotalLength: readTotal,
	invalidateData: invalidateRead,
} = useTable({
	definition: $collections.notificationsRead,
	// preloadAllPages: true,
})
const {
	itemsTotal: unredItems,
	isLoading: unredLoading,
	itemsTotalLength: unredTotal,
	invalidateData: invalidateUnread,
} = useTable({
	definition: $collections.notificationsUnread,
	// preloadAllPages: true,
})
const {
	itemsTotal: baseItems,
	isLoading: baseLoading,
	itemsTotalLength: baseTotal,
	invalidateData: invalidateBase,
} = useTable({
	definition: $collections.notificationsBase,
	// preloadAllPages: true,
})

const notificationsCollection = computed(() =>
	mode.value === 0
		? unredItems.value
		: mode.value === 1
			? readItems.value
			: baseItems.value
)
const notifications = ref([])

const itemsTotalLength = computed(() =>
	mode.value === 0
		? unredTotal.value
		: mode.value === 1
			? readTotal.value
			: baseTotal.value
)

const loading = computed(() =>
	mode.value === 0
		? unredLoading.value
		: mode.value === 1
			? readLoading.value
			: baseLoading.value
)

const notificationsCountUnread = computed(() => unredTotal.value)

const status = computed(() =>
	mode.value === 0
		? t('component.notifications.new')
		: mode.value === 1
			? t('component.notifications.read').toLowerCase()
			: ''
)

function reloadData() {
	invalidateBase()
	invalidateRead()
	invalidateUnread()
}

watch(
	notificationsCollection,
	async (newNotifications) => {
		if (!newNotifications) return

		try {
			notifications.value = []
			for (const notification of newNotifications) {
				//TODO: the caching of loaded users doesn't work, requests are still made
				const notify = await buildNotificationContent(notification)
				if (typeof notify.content === 'undefined') {
					notify.content = {}
				}
				notify.content.read_at = notification.read_at
				notify.content.created_at = notification.created_at
				notifications.value.push(notify)
			}
		} catch (error) {
			console.error('Error processing notifications:', error)
			notifications.value = []
		}
	},
	{ immediate: true }
)

onBeforeUnmount(() => {
	clearInterval(polling.value)
})

onBeforeMount(() => {
	pollCount()
})

function available() {
	return unredTotal.value > 0
}

async function buildNotificationContent(notification: Api.Notification) {
	let { icon, title, link } = structuredClone(
		getNotificationType(notification.type).value
	)
	let { id } = notification
	switch (notification.type) {
		case 'App\\Notifications\\Welcome':
		case 'App\\Notifications\\LostPassword':
		case 'App\\Notifications\\Message':
		case 'App\\Notifications\\DamageReport':
		case 'App\\Notifications\\ReportNotification':
			return { content: await buildPersonLink(notification), icon, title, id }
		case 'App\\Notifications\\Notes':
		case 'App\\Notifications\\NotesAllUsers':
			link.to.params = { noteId: notification.data.note.id }
			link.name = notification.data.note.subject
			return { content: { link }, icon, title, id }
		case 'App\\Notifications\\PrivacyTosRejected':
			link.to.params = { id: notification.id }
			return { content: { link }, icon, title, id }
		case 'App\\Notifications\\TicketHistoryNotification':
			link.name = `Ticket ${notification.data.ticket.hp_id}`
			link.to.params = { id: notification.data.ticket.id }
			return { content: { link }, icon, title, id }
		case 'App\\Notifications\\MeterLevelDaily':
		case 'App\\Notifications\\QuotaExceededNotification':
		case 'App\\Notifications\\HandOverInvitation':
		case 'App\\Notifications\\MessageDone':
		case 'App\\Notifications\\PersonUpdateRequest':
		case 'App\\Notifications\\PersonUpdateRequestAdmin':
			return { content: { link }, icon, title, id }
		case 'App\\Notifications\\ConsumptionNotification':
			link.to.params = {
				unitId: notification.data.meter.unit_id,
				id: notification.data.meter.building_id,
			}
			return { content: { link }, icon, title, id }
		case 'App\\Notifications\\DocumentUserNotification': {
			let {
				documents_updated_yesturday_building,
				documents_created_yesturday_building,
				documents_created_yesturday_unit,
				documents_updated_yesturday_unit,
			} = notification.data
			let ids = documents_created_yesturday_building.concat(
				documents_created_yesturday_unit,
				documents_updated_yesturday_building,
				documents_updated_yesturday_unit
			)
			ids = ids.filter((v, i) => ids.indexOf(v) === i)
			link.to.params = { ids }
			return { content: { link }, icon, title, id }
		}
		case 'App\\Notifications\\MeterLevelRejected':
			return { content: await buildPersonLink(notification), icon, title, id }
		case 'App\\Notifications\\SyncStatusNotification':
			link.to.params = { id: notification.data.sync_id }
			return { content: { link }, icon, title, id }
		case 'App\\Notifications\\ReviewDocumentNotification':
			title = notification.data.template.subject
			link.to.params = {
				reviewDocumentId: notification.data.reviewDocument.id,
			}
			return { content: { link }, icon, title, id }
		default:
			icon = 'fa-regular fa-question'
			title = notification.type
			return { content: { link }, icon, title, id }
	}
}

function markAllAsRead() {
	updating.value = true
	notificationStore.markAllRead().then(() => {
		reloadData()
		notifications.value = []
		updating.value = false
	})
}

function markAsRead(id) {
	notifications.value.splice(
		notifications.value.findIndex((notification) => notification.id === id),
		1
	)
	notificationStore.mark({ id }).then(() => {
		reloadData()
	})
}

function pollCount() {
	// if (!polling.value && !import.meta.env.DEV) {
	// 	polling.value = setInterval(() => {
	//
	// 	}, 300000)
	// }
}

function notificationLink(id) {
	markAsRead(id)
	menu.value = false
}

async function buildPersonLink(notification) {
	let content
	if (typeof notification.data.user.person !== 'undefined') {
		content = personLink(notification, notification.data.user.person)
		return content
	}
	if (isTeam.value || isAdmin.value) {
		const data = await usersStore.show({ id: notification.data.user.id })
		if (data?.persons?.length) {
			content = personLink(notification, data.persons[0])
			return content
		} else {
			return personLinkDefault
		}
	} else {
		let person = whoami.value?.persons.find(
			(x) => x.user_id === notification.data.user.id
		)
		if (person) {
			content = personLink(notification, person)
			return content
		}
	}
}
function personLink(notification, person) {
	if (person) {
		return {
			link: {
				to: {
					name: 'person',
					params: { id: person.id },
				},
				name: personBuildFullname(person),
			},
		}
	} else {
		return personLinkDefault
	}
}
</script>

<style lang="scss" scoped>
#notifications-card {
	.notifications-card__subtitle {
		max-width: inherit;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.notifications-card__mode,
	.notifications-card__title {
		background: white;
		position: sticky;
		z-index: 1;
	}

	.notifications-card__title {
		top: 0;
		border-bottom: 1px solid rgba(0, 0, 0, 0.12);
	}

	.notifications-card__mode {
		border-top: 1px solid rgba(0, 0, 0, 0.12);
		bottom: 0;
		min-height: 50px;
	}
}
</style>
